import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateOrganizationDto } from "../../core/models/dtos/CreateCustomerDto";
import { UpdateOrganizationDto } from "../../core/models/dtos/UpdateCustomerDto";
import { Organization } from "../../core/models/entities/Organization";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import { mapToOrganization } from "../models/mappers/mapOrganization";
import { PageQuery, toQueryParam } from "../../utils/models/pageQuery";
import { PagedResponse } from "../models/responses/pagedResponse";
import { BillingOverviewDto } from "../models/responses/BillingOverviewDto";
import { BillingVendorGroupDto } from "../models/responses/BillingVendorGroupDto";
import { mapFromCreateOrganizationDto } from "../models/mapFromCreateOrganizationDto";
import { mapFromUpdateOrganizationDto } from "../models/mapFromUpdateOrganizationDto";
import { CommissionProfile } from "../../core/models/entities/Commission";
import { mapToCommissionProfilePayload } from "../models/mappers/mapCommissions";

export interface OrganizationRepository {
  useAllCustomers(query: PageQuery): Resource<PagedResponse<Organization>>;
  useAllVendors(query: PageQuery): Resource<PagedResponse<Organization>>;
  /**
   * Admin billing overview: which organizations have a billing vendor
   * attached (the invoicing hierarchy). Server-paged/searchable/sortable —
   * DTO consumed as-is. Optional billingVendorOrgId narrows to one vendor's
   * organizations (the drill-down view). keepPreviousData keeps the current
   * page visible while the next one loads. Throws through to an error state
   * until the endpoint is live.
   */
  useBillingOverview(
    query: PageQuery,
    billingVendorOrgId?: number,
  ): Resource<PagedResponse<BillingOverviewDto>>;
  /**
   * The grouped/reversed billing view: one row per billing vendor ("mother")
   * with counts of attached organizations. Same paging/search/error contract
   * as useBillingOverview.
   */
  useBillingVendorGroups(
    query: PageQuery,
  ): Resource<PagedResponse<BillingVendorGroupDto>>;
  prefetchAllCustomers(query: PageQuery): Promise<void>;
  prefetchAllVendors(query: PageQuery): Promise<void>;
  useOrganization(id: number): Resource<Organization>;
  useCustomer(id: number): Resource<Organization>;
  useVendor(id?: number): Resource<Organization>;
  getAllCustomers(): Promise<Organization[]>;
  getAllVendors(): Promise<Organization[]>;
  searchAdminVendors(searchQuery: string): Promise<Organization[]>;
  getOrganizationById(id: number): Promise<Organization>;
  getVendorById(id: number): Promise<Organization>;
  getCustomerById(id: number): Promise<Organization>;
  updateOrganization(dto: UpdateOrganizationDto): Promise<void>;
  createOrganization(
    dto: CreateOrganizationDto,
    companyLogo?: File
  ): Promise<void>;
  deleteOrganization(id: number): Promise<void>;
  updateCustomer(organization: Organization): Promise<Organization>;
  createCustomer(organization: Organization): Promise<Organization>;
  deleteCustomer(organization: Organization): Promise<void>;
  updateOrganizationLogo(
    organizationId: number,
    companyLogo: File
  ): Promise<void>;
  deleteOrganizationLogo(organizationId: number): Promise<void>;
  updateProviderLogo(companyLogo: File): Promise<void>;
  setCommissionProfile(
    orgId: number,
    commissionProfile: CommissionProfile
  ): Promise<void>;
}

export function useOrganizationRepository(): OrganizationRepository {
  const apiClient = useApiClient();

  const queryClient = useQueryClient();
  async function invalidateCustomer(id: Organization["id"]) {
    await queryClient.refetchQueries({
      queryKey: ["organization", id],
      type: "all",
      refetchPage: () => true,
    });
    await queryClient.refetchQueries({
      queryKey: ["customer", id],
      type: "all",
      refetchPage: () => true,
    });
    await invalidateCustomers();
  }

  async function invalidateCustomers() {
    await queryClient.refetchQueries({
      queryKey: ["all-customers"],
      type: "all",
      refetchPage: () => true,
    });
  }

  function useOrganization(id: Organization["id"]): Resource<Organization> {
    return useQuery(["organization", id], () =>
      apiClient.getOrganization(id).then((data) => mapToOrganization(data))
    );
  }

  function useCustomer(id: Organization["id"]): Resource<Organization> {
    return useQuery(["customer", id], () =>
      apiClient.getCustomer(id).then((data) => mapToOrganization(data))
    );
  }

  function useVendor(id?: Organization["id"]): Resource<Organization> {
    return useQuery(["vendor", id], () => {
      if (!id) {
        return null;
      }
      return apiClient.getVendor(id).then((data) => mapToOrganization(data));
    });
  }

  async function getOrganizationById(
    id: Organization["id"]
  ): Promise<Organization> {
    const response = await apiClient.getOrganization(id);
    return mapToOrganization(response);
  }

  async function getVendorById(id: Organization["id"]): Promise<Organization> {
    return apiClient.getVendor(id).then((data) => mapToOrganization(data));
  }

  async function getCustomerById(
    id: Organization["id"]
  ): Promise<Organization> {
    return apiClient.getCustomer(id).then((data) => mapToOrganization(data));
  }

  function useAllCustomers(
    query: PageQuery
  ): Resource<PagedResponse<Organization>> {
    return useQuery(["all-customers", toQueryParam(query)], () =>
      apiClient.getAllCustomers(query).then((data) => ({
        ...data,
        results: data.results.map(mapToOrganization),
      }))
    );
  }

  function useAllVendors(
    query: PageQuery
  ): Resource<PagedResponse<Organization>> {
    return useQuery(["all-vendors", toQueryParam(query)], () =>
      apiClient.getAllVendors(query).then((data) => ({
        ...data,
        results: data.results.map(mapToOrganization),
      }))
    );
  }

  function useBillingOverview(
    query: PageQuery,
    billingVendorOrgId?: number
  ): Resource<PagedResponse<BillingOverviewDto>> {
    return useQuery(
      // null (not undefined) in the key: undefined segments are dropped by
      // React Query's key hashing, which would collide the unfiltered list
      // with a drill-down list.
      ["billing-overview", toQueryParam(query), billingVendorOrgId ?? null],
      () => apiClient.getBillingOverview(query, billingVendorOrgId),
      {
        // Admin surface — reflect the current server state on entry, and keep
        // the previous page visible while the next one loads.
        staleTime: 0,
        keepPreviousData: true,
      }
    );
  }

  function useBillingVendorGroups(
    query: PageQuery
  ): Resource<PagedResponse<BillingVendorGroupDto>> {
    return useQuery(
      ["billing-vendors", toQueryParam(query)],
      () => apiClient.getBillingVendorGroups(query),
      {
        // Same admin-surface behavior as useBillingOverview.
        staleTime: 0,
        keepPreviousData: true,
      }
    );
  }

  // Cache-warming twins of useAllCustomers/useAllVendors (same key and
  // queryFn, default options) so the key construction lives in one place.
  // Fail-silent: on a miss the hooks fetch as they do today.
  function prefetchAllCustomers(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(["all-customers", toQueryParam(query)], () =>
      apiClient.getAllCustomers(query).then((data) => ({
        ...data,
        results: data.results.map(mapToOrganization),
      }))
    );
  }

  function prefetchAllVendors(query: PageQuery): Promise<void> {
    return queryClient.prefetchQuery(["all-vendors", toQueryParam(query)], () =>
      apiClient.getAllVendors(query).then((data) => ({
        ...data,
        results: data.results.map(mapToOrganization),
      }))
    );
  }

  async function getAllCustomers(): Promise<Organization[]> {
    const response = await apiClient.getAllCustomers({
      page: { size: 0, index: 0 },
    } as PageQuery);
    return response.results.map(mapToOrganization);
  }

  async function getAllVendors(): Promise<Organization[]> {
    const response = await apiClient.getAllVendors({
      page: { size: 0, index: 0 },
    } as PageQuery);
    return response.results.map(mapToOrganization);
  }

  async function searchAdminVendors(
    searchQuery: string
  ): Promise<Organization[]> {
    const response = await apiClient.searchAdminVendors(searchQuery);
    return response.map(mapToOrganization);
  }

  async function updateOrganization(
    dto: UpdateOrganizationDto,
    companyLogo?: File
  ): Promise<void> {
    return await apiClient
      .updateOrganization(dto.id, mapFromUpdateOrganizationDto(dto))
      .then(async (res) => {
        if (res.isVendor && companyLogo) {
          await apiClient.uploadCompanyLogo(res.id, companyLogo);
        }
      });
  }

  async function updateOrganizationLogo(
    organizationId: Organization["id"],
    companyLogo: File
  ): Promise<void> {
    return await apiClient.uploadCompanyLogo(organizationId, companyLogo);
  }

  async function deleteOrganizationLogo(
    organizationId: Organization["id"]
  ): Promise<void> {
    return await apiClient.deleteCompanyLogo(organizationId);
  }

  async function updateProviderLogo(companyLogo: File): Promise<void> {
    return await apiClient.uploadProviderLogo(companyLogo);
  }

  function createCustomer(organization: Organization): Promise<Organization> {
    return apiClient.createCustomer(organization).then(mapToOrganization);
  }

  function updateCustomer(organization: Organization): Promise<Organization> {
    return apiClient
      .updateCustomer(organization)
      .then(mapToOrganization)
      .finally(() => invalidateCustomer(organization.id));
  }

  function deleteCustomer(organization: Organization): Promise<void> {
    return apiClient.deleteCustomer(organization);
  }

  async function createOrganization(
    dto: CreateOrganizationDto,
    companyLogo?: File
  ): Promise<void> {
    return await apiClient
      .createOrganization(mapFromCreateOrganizationDto(dto))
      .then(async (res) => {
        if (res.isVendor && companyLogo) {
          await apiClient.uploadCompanyLogo(res.id, companyLogo);
        }
      });
  }

  function deleteOrganization(id: number): Promise<void> {
    return apiClient.deleteOrganization(id);
  }

  async function setCommissionProfile(
    orgId: number,
    commissionProfile: CommissionProfile
  ): Promise<void> {
    if (
      !commissionProfile.overAll &&
      !commissionProfile.additionalFees &&
      !commissionProfile.heating &&
      commissionProfile.methods.length === 0 &&
      commissionProfile.typeOnly.length === 0
    ) {
      await apiClient.deleteCommissionProfile(orgId);
    } else {
      // Normalize method rules to the wire shape the API price engine
      // matches (cm2 buckets as maxSizeCM2 + priceType, not as the
      // "100 cm²" display label the editors key rows on).
      await apiClient.setCommissionProfile(
        orgId,
        mapToCommissionProfilePayload(commissionProfile)
      );
    }
    await invalidateCustomer(orgId);
  }

  return {
    useAllVendors,
    useAllCustomers,
    useBillingOverview,
    useBillingVendorGroups,
    prefetchAllCustomers,
    prefetchAllVendors,
    useOrganization,
    useCustomer,
    useVendor,
    getAllVendors,
    getAllCustomers,
    searchAdminVendors,
    getOrganizationById,
    getCustomerById,
    getVendorById,
    updateOrganization,
    createOrganization,
    deleteOrganization,
    updateOrganizationLogo,
    deleteOrganizationLogo,
    updateProviderLogo,
    createCustomer,
    deleteCustomer,
    updateCustomer,
    setCommissionProfile,
  };
}
